import React, { useMemo } from "react";
import {
  formatMembershipAmount,
  formatMembershipDate,
  membershipStatusBadgeClass,
} from "../../utils/myMembershipsDirectoryHelpers";

function resolveSummary(summary, timeline) {
  if (!summary) return null;
  const primary =
    timeline?.find((t) => t.membership_id === summary.membership_id) ||
    timeline?.[0];
  if (!primary) return summary;

  return {
    ...summary,
    plan_name: summary.plan_name || primary.plan_name,
    plan_price: summary.plan_price ?? primary.plan_price,
    payment_id: summary.payment_id ?? primary.payment_id,
    payment_amount: summary.payment_amount ?? primary.payment_amount,
    membership_fee:
      summary.membership_fee ??
      primary.payment_amount ??
      primary.plan_price ??
      null,
    payment_status:
      summary.payment_status && summary.payment_status !== "No payment"
        ? summary.payment_status
        : primary.payment_status || summary.payment_status,
    payment_verified: summary.payment_verified ?? primary.payment_verified,
    receipt_no: summary.receipt_no || primary.receipt_no,
    transaction_id: summary.transaction_id || primary.transaction_id,
    payment_date: summary.payment_date || primary.payment_date,
    payment_mode: summary.payment_mode || primary.payment_mode,
  };
}

function payChipClass(status) {
  return `mm-pay-chip mm-pay-chip--${String(status || "none")
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
}

function MetaItem({ label, value, emphasis }) {
  return (
    <div className={`mm-summary-meta${emphasis ? " mm-summary-meta--emphasis" : ""}`}>
      <span className="mm-summary-meta__label">{label}</span>
      <span className="mm-summary-meta__value">{value}</span>
    </div>
  );
}

export default function MyMembershipsSummarySection({ summary, timeline, loading }) {
  const resolved = useMemo(
    () => resolveSummary(summary, timeline),
    [summary, timeline]
  );

  if (loading) {
    return (
      <section className="user-builder-panel mm-summary-panel" aria-label="Membership summary">
        <div className="mm-summary-skeleton" aria-hidden />
      </section>
    );
  }

  if (!resolved) {
    return (
      <section className="user-builder-panel mm-summary-panel" aria-label="Membership summary">
        <p className="mm-empty">No membership records to summarise yet.</p>
      </section>
    );
  }

  const status = resolved.validity_status;
  const statusKey = String(status || "muted").toLowerCase();
  const amountPaid = formatMembershipAmount(
    resolved.payment_amount ?? resolved.membership_fee
  );
  const planPrice = formatMembershipAmount(resolved.plan_price);
  const hasPayment =
    resolved.payment_id != null ||
    resolved.payment_amount != null ||
    resolved.payment_status === "Paid" ||
    resolved.payment_status === "Pending";

  const validityText = `${formatMembershipDate(resolved.membership_start_date)} → ${formatMembershipDate(resolved.membership_end_date)}`;

  return (
    <section className="user-builder-panel mm-summary-panel" aria-label="Membership summary">
      <div className="user-builder-panel__head">
        <div>
          <h3 className="user-builder-panel__title">Current membership</h3>
          <p className="user-builder-panel__hint">
            Latest active or most recent plan from your applications and renewals.
          </p>
        </div>
      </div>

      <article className={`mm-summary-card mm-summary-card--${statusKey}`}>
        <div className="mm-summary-card__top">
          <div className="mm-summary-card__identity">
            <span className="mm-summary-card__plan">
              {resolved.plan_name || resolved.membership_type || "Membership"}
            </span>
            <span className="mm-summary-card__id">{resolved.membership_id || "—"}</span>
          </div>
          <div className="mm-summary-card__badges">
            <span className={membershipStatusBadgeClass(status)}>{status || "—"}</span>
            {resolved.renewal_status ? (
              <span className="mm-summary-card__renewal">{resolved.renewal_status}</span>
            ) : null}
          </div>
        </div>

        <div className="mm-summary-card__stats">
          <MetaItem label="Validity" value={validityText} />
          <MetaItem
            label="Amount paid"
            value={hasPayment ? amountPaid : "—"}
            emphasis
          />
          <MetaItem label="Plan price" value={planPrice} />
          <div className="mm-summary-meta mm-summary-meta--status">
            <span className="mm-summary-meta__label">Payment</span>
            <span className={payChipClass(resolved.payment_status)}>
              {resolved.payment_status || "No payment"}
            </span>
          </div>
        </div>

        {hasPayment ? (
          <footer className="mm-summary-card__footer">
            <span>
              <strong>Date</strong> {formatMembershipDate(resolved.payment_date)}
            </span>
            <span className="mm-summary-card__dot" aria-hidden>
              ·
            </span>
            <span>
              <strong>Mode</strong> {resolved.payment_mode || "—"}
            </span>
            <span className="mm-summary-card__dot" aria-hidden>
              ·
            </span>
            <span>
              <strong>Txn</strong>{" "}
              <code>{resolved.transaction_id || "—"}</code>
            </span>
            {resolved.receipt_no ? (
              <>
                <span className="mm-summary-card__dot" aria-hidden>
                  ·
                </span>
                <span>
                  <strong>Receipt</strong> <code>{resolved.receipt_no}</code>
                </span>
              </>
            ) : null}
          </footer>
        ) : null}
      </article>
    </section>
  );
}
